import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Code, ChevronDown, ChevronRight, Copy, Check, Search,
  Loader2, BookOpen, Zap, Lock, Globe, Server
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_COLORS = {
  GET: 'bg-green-500/20 text-green-400 border-green-500/30',
  POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  PUT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const API_SECTIONS = [
  {
    title: 'Authentication',
    icon: Lock,
    color: 'from-red-500 to-rose-500',
    endpoints: [
      { method: 'POST', path: '/api/auth/register', desc: 'Register new user', auth: false, body: '{"email", "password", "name"}' },
      { method: 'POST', path: '/api/auth/login', desc: 'Login and get access token', auth: false, body: '{"email", "password"}' },
      { method: 'GET', path: '/api/auth/me', desc: 'Get current user profile', auth: true },
    ]
  },
  {
    title: 'Subjects & Lectures',
    icon: BookOpen,
    color: 'from-blue-500 to-cyan-500',
    endpoints: [
      { method: 'GET', path: '/api/subjects', desc: 'List all subjects', auth: true },
      { method: 'POST', path: '/api/subjects', desc: 'Create a subject', auth: true, body: '{"name", "color", "description"}' },
      { method: 'GET', path: '/api/lectures', desc: 'List all lectures', auth: true },
      { method: 'POST', path: '/api/lectures?auto_generate=true', desc: 'Create lecture (optionally auto-generate flashcards)', auth: true, body: '{"subject_id", "title", "topic", "notes", "transcript"}' },
    ]
  },
  {
    title: 'AI Features',
    icon: Zap,
    color: 'from-purple-500 to-indigo-500',
    endpoints: [
      { method: 'POST', path: '/api/transcribe', desc: 'Transcribe audio file (Whisper)', auth: true, body: 'FormData: file (audio)' },
      { method: 'POST', path: '/api/simplify-text', desc: 'Simplify complex text', auth: true, body: '{"transcript_snippet", "timestamp"}' },
      { method: 'POST', path: '/api/summarize-lecture', desc: 'Generate AI summary', auth: true, body: '{"transcript", "title"}' },
      { method: 'POST', path: '/api/study-buddy/chat', desc: 'Chat with AI study buddy', auth: true, body: '{"messages": [{role, content}]}' },
      { method: 'POST', path: '/api/learning-bytes/generate/{lecture_id}', desc: 'Generate flashcards from lecture', auth: true },
    ]
  },
  {
    title: 'Spaced Repetition (SRS)',
    icon: Server,
    color: 'from-amber-500 to-orange-500',
    endpoints: [
      { method: 'GET', path: '/api/srs/due', desc: 'Get items due for review', auth: true },
      { method: 'POST', path: '/api/srs/review/{byte_id}?quality=4', desc: 'Submit review (SM-2 algorithm, quality 0-5)', auth: true },
      { method: 'GET', path: '/api/srs/weakness-map', desc: 'Get weakness breakdown by subject', auth: true },
      { method: 'GET', path: '/api/srs/stats', desc: 'Get SRS statistics', auth: true },
      { method: 'GET', path: '/api/srs/next-set?count=10', desc: 'Get recommended study set', auth: true },
    ]
  },
  {
    title: 'Exam Mode',
    icon: Server,
    color: 'from-green-500 to-emerald-500',
    endpoints: [
      { method: 'GET', path: '/api/exam-mode/subjects', desc: 'Get subjects with quiz questions', auth: true },
      { method: 'POST', path: '/api/exam-mode/start?subject_id=...&question_count=10', desc: 'Start timed exam', auth: true },
      { method: 'POST', path: '/api/exam-mode/submit', desc: 'Submit exam answers', auth: true, body: '{"exam_id", "answers": {qid: answer}, "time_taken_seconds"}' },
      { method: 'GET', path: '/api/exam-mode/history', desc: 'Get past exam results', auth: true },
      { method: 'GET', path: '/api/exam-mode/question-bank', desc: 'View all quiz questions', auth: true },
      { method: 'POST', path: '/api/exam-mode/questions', desc: 'Add question manually', auth: true, body: '{"subject_id", "question", "options", "correct", "explanation"}' },
      { method: 'PUT', path: '/api/exam-mode/questions/{id}', desc: 'Edit question', auth: true },
      { method: 'DELETE', path: '/api/exam-mode/questions/{id}', desc: 'Delete question', auth: true },
      { method: 'POST', path: '/api/exam-mode/import', desc: 'Import exam paper (PDF/text)', auth: true, body: 'FormData: file, subject_id' },
    ]
  },
  {
    title: 'Campus Plans (B2B)',
    icon: Globe,
    color: 'from-indigo-500 to-violet-500',
    endpoints: [
      { method: 'GET', path: '/api/campus/plans', desc: 'Get campus pricing plans', auth: false },
      { method: 'POST', path: '/api/campus/register', desc: 'Register campus organization', auth: true, body: '{"name", "institution", "plan_id", "admin_email"}' },
      { method: 'GET', path: '/api/campus/my-campus', desc: 'Get your campus info', auth: true },
      { method: 'GET', path: '/api/campus/members', desc: 'List campus members (admin)', auth: true },
      { method: 'POST', path: '/api/campus/invite', desc: 'Invite members by email', auth: true, body: '{"emails": ["..."]}' },
      { method: 'POST', path: '/api/campus/import-csv', desc: 'Bulk import from CSV', auth: true, body: 'FormData: file (CSV with email column)' },
      { method: 'GET', path: '/api/campus/export-report?format=csv', desc: 'Export usage report', auth: true },
      { method: 'GET', path: '/api/campus/analytics', desc: 'Campus usage analytics', auth: true },
      { method: 'GET', path: '/api/campus/leaderboard', desc: 'Campus leaderboard', auth: true },
    ]
  },
  {
    title: 'Gamification',
    icon: Server,
    color: 'from-pink-500 to-rose-500',
    endpoints: [
      { method: 'GET', path: '/api/gamification/profile', desc: 'Get XP and level', auth: true },
      { method: 'GET', path: '/api/gamification/badges', desc: 'List all badges', auth: true },
      { method: 'GET', path: '/api/gamification/streak', desc: 'Get study streak info', auth: true },
      { method: 'POST', path: '/api/gamification/streak/checkin', desc: 'Daily streak check-in', auth: true },
      { method: 'GET', path: '/api/gamification/leaderboard?period=all', desc: 'Global leaderboard', auth: true },
    ]
  },
];

const EndpointRow = ({ ep }) => {
  const [copied, setCopied] = useState(false);

  const copyPath = () => {
    navigator.clipboard.writeText(`${BACKEND_URL}${ep.path}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)] hover:border-[var(--isp-glass-border)] transition-all" data-testid={`endpoint-${ep.method}-${ep.path.replace(/\//g, '-')}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold border ${METHOD_COLORS[ep.method]}`}>
          {ep.method}
        </span>
        <code className="text-xs text-[var(--isp-body)] font-mono truncate">{ep.path}</code>
        {ep.auth && <Lock className="w-3 h-3 text-amber-400/60 flex-shrink-0" title="Requires auth" />}
      </div>
      <div className="flex items-center gap-2">
        <p className="text-xs text-[var(--isp-body)] flex-1">{ep.desc}</p>
        <button aria-label="Confirm" onClick={copyPath} className="p-1.5 rounded-lg hover:bg-[var(--isp-glass-inner)] text-[var(--isp-muted)] hover:text-[var(--isp-heading)] transition-all flex-shrink-0">
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      {ep.body && <p className="text-[10px] text-[var(--isp-muted)] font-mono pl-14 sm:pl-0">{ep.body}</p>}
    </div>
  );
};

const ApiDocsPage = () => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const toggleSection = (title) => {
    setExpanded(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const filtered = search
    ? API_SECTIONS.map(s => ({
        ...s,
        endpoints: s.endpoints.filter(ep =>
          ep.path.toLowerCase().includes(search.toLowerCase()) ||
          ep.desc.toLowerCase().includes(search.toLowerCase())
        )
      })).filter(s => s.endpoints.length > 0)
    : API_SECTIONS;

  return (
    <div className="f500-page space-y-6" data-testid="api-docs-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
            <Code className="w-6 h-6 text-white" />
          </div>
          API Documentation
        </h1>
        <p className="text-[var(--isp-body)] mt-2">
          Base URL: <code className="text-purple-400 text-sm">{BACKEND_URL}/api</code>
        </p>
      </div>

      {/* Auth info */}
      <Card className="p-4  bg-amber-500/10 border-amber-500/20">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-300">Authentication</p>
            <p className="text-xs text-amber-300/70 mt-1">
              Endpoints marked with <Lock className="w-3 h-3 inline text-amber-400" /> require a Bearer token.
              Send <code className="text-amber-200">Authorization: Bearer &lt;access_token&gt;</code> header.
              Get your token from <code className="text-amber-200">POST /api/auth/login</code>.
            </p>
          </div>
        </div>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--isp-muted)]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search endpoints..."
          className="w-full pl-11 pr-4 py-3 rounded-xl bg-[var(--isp-glass)] backdrop-blur-xl border border-[var(--isp-glass-border)] text-white text-sm focus:border-purple-500/50 focus:outline-none"
          data-testid="search-endpoints"
        />
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {filtered.map((section) => {
          const Icon = section.icon;
          const isOpen = expanded[section.title] !== false; // Default open
          return (
            <Card key={section.title} className=" bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] overflow-hidden" data-testid={`section-${section.title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center gap-3 p-4 hover:bg-[var(--isp-glass-inner)] transition-all"
              >
                <div className={`p-2 rounded-xl bg-gradient-to-br ${section.color}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h2 className="text-base font-semibold text-white">{section.title}</h2>
                  <p className="text-xs text-[var(--isp-muted)]">{section.endpoints.length} endpoint{section.endpoints.length !== 1 ? 's' : ''}</p>
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-[var(--isp-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--isp-muted)]" />}
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 space-y-2">
                      {section.endpoints.map((ep, i) => (
                        <EndpointRow key={`item-${i}`} ep={ep} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ApiDocsPage;
